import React, { useState } from 'react'
import { CreditApplication } from '_gql'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'

import { StatusButtons } from '@sputnik/components'
import ImageWrapper from '@sputnik/components/ImageWrapper/ImageWrapper'
import { InviteCollaboratorsDrawer } from '@sputnik/components/InviteCollaborators'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logInfo } from '@sputnik/utils/loggerUtils'

interface NotStartedOrInProgressStatusTileProps {
  creditApp?: CreditApplication
}

// This status shows if status is not_started or if in_progress without savedSections
const NotStartedOrInProgressStatusTile = ({ creditApp }: NotStartedOrInProgressStatusTileProps) => {
  const makeTestId = useMakeTestId('NotStartedOrInProgressStatusTile')
  const { salesforceCreditId } = creditApp || {}

  const [isShowingInviteCollaborators, setIsShowingInviteCollaborators] = useState(false)
  const logApplicationStart = () => {
    logInfo('creditApplicationStarted', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: 'Successfully started a credit application',
      },
      eventName: 'ApplicationStarted',
    })
  }
  return (
    <div data-testid={makeTestId('')}>
      <StatusTile
        creditApp={creditApp}
        image={
          <ImageWrapper
            alt='man with a clipboard'
            imageName='dashboard-application-start-status'
          />
        }
        primaryInfo={
          <FormattedMessage
            defaultMessage='Submit an application in 9-12 minutes'
            id='sputnik.NotStartedOrInProgressStatusTile__dPMaeT'
          />
        }
        secondaryInfo={
          <>
            <div>
              <FormattedMessage
                defaultMessage='To speed things along, be sure to have...'
                id='sputnik.NotStartedOrInProgressStatusTile__QgD9Ap'
              />
            </div>
            <ul className='status-description-items'>
              <li>
                <FormattedMessage
                  defaultMessage='Authorized signer’s information'
                  id='sputnik.NotStartedOrInProgressStatusTile__QL6GAm'
                />
              </li>
              <li>
                <FormattedMessage
                  defaultMessage='Company owner’s information (if applicable)'
                  id='sputnik.NotStartedOrInProgressStatusTile__lYoau7'
                />
              </li>
              <li>
                <FormattedMessage
                  defaultMessage='Business bank account information'
                  id='sputnik.NotStartedOrInProgressStatusTile__54dHhO'
                />
              </li>
              <li>
                <FormattedMessage
                  defaultMessage='Business tax ID number & formation date'
                  id='sputnik.NotStartedOrInProgressStatusTile__xr7Wkx'
                />
              </li>
            </ul>
          </>
        }
        title={
          <FormattedMessage
            defaultMessage='Start application'
            id='sputnik.NotStartedOrInProgressStatusTile__fl/mYL'
          />
        }
      >
        <StatusButtons
          hideSecondary={!salesforceCreditId}
          primaryPath={
            salesforceCreditId
              ? `/app/${salesforceCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`
              : PATHNAME_CONSTS.GET_TO_KNOW_YOU
          }
          primaryText={
            <FormattedMessage
              defaultMessage='Start application'
              id='sputnik.NotStartedOrInProgressStatusTile__fl/mYL'
            />
          }
          onPrimaryClick={logApplicationStart}
          {...(salesforceCreditId && {
            secondaryText: (
              <FormattedMessage
                defaultMessage='Invite collaborators'
                id='sputnik.NotStartedOrInProgressStatusTile__ni18Y2'
              />
            ),
            onSecondaryClick: () => setIsShowingInviteCollaborators(true),
          })}
        />
        {salesforceCreditId && (
          <InviteCollaboratorsDrawer
            appId={salesforceCreditId}
            isShowing={isShowingInviteCollaborators}
            onClose={() => setIsShowingInviteCollaborators(false)}
          />
        )}
      </StatusTile>
    </div>
  )
}

export default NotStartedOrInProgressStatusTile
